import { createRoute } from "@tanstack/react-router";
import { FormEvent, useEffect, useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { useAuth } from "../auth/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { rootRoute } from "./__root";

interface ScoringPayload {
  base: Record<string, number>;
  rules: Array<{
    id: number;
    field_type: string;
    field_value: string;
    weight: number;
    reason_label: string | null;
    is_active: boolean;
  }>;
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/scoring",
  component: ScoringScreen,
});

function ScoringScreen() {
  return (
    <ProtectedRoute requiredRoles={["owner"]}>
      <ScoringContent />
    </ProtectedRoute>
  );
}

function ScoringContent() {
  const { accessToken } = useAuth();
  const [data, setData] = useState<ScoringPayload | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    fetch("/api/cockpit/settings/scoring", {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: "include",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [accessToken]);

  async function saveBase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!accessToken || !data) return;
    const form = new FormData(event.currentTarget);
    const base = Object.fromEntries(
      Object.keys(data.base).map((key) => [key, Number(form.get(key))]),
    );
    setSaving(true);
    const res = await fetch("/api/cockpit/settings/scoring", {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ base }),
    });
    if (res.ok) setData(await res.json());
    setSaving(false);
  }

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Settings</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">Scoring</h1>
        <p className="mt-1 text-sm text-slate-600">Tune tenant lead scoring weights and priority thresholds.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Base scoring parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveBase} className="grid gap-3 md:grid-cols-4">
            {Object.entries(data?.base || {}).map(([key, value]) => (
              <label key={key} className="text-xs font-medium text-slate-600">
                {key.replace(/^scoring_/, "").replace(/_/g, " ")}
                <input
                  name={key}
                  type="number"
                  defaultValue={value}
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                />
              </label>
            ))}
            <div className="md:col-span-4">
              <Button disabled={saving || !data} type="submit">Save thresholds</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="py-2">Type</th>
                  <th>Value</th>
                  <th>Weight</th>
                  <th>Reason</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rules || []).map((rule) => (
                  <tr key={rule.id} className="border-b last:border-0">
                    <td className="py-3">{rule.field_type}</td>
                    <td>{rule.field_value}</td>
                    <td>{rule.weight}</td>
                    <td>{rule.reason_label || "-"}</td>
                    <td>{rule.is_active ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
